import { Flex, FormControl, FormLabel, Text } from "@chakra-ui/react";
import { FormikProps } from "formik";
import { CheckboxControl } from "formik-chakra-ui";
import { useEffect } from "react";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import {
  SCHOOL_CONFLICT_SUMMARY,
  useFieldError,
  useSchoolErrorById,
} from "src/scenes/orgAdmin/formTemplates/RulesTab/FormEngineRuleDrawer/context";
import { FormEngineRuleFormData } from "src/scenes/orgAdmin/formTemplates/RulesTab/FormEngineRuleDrawer/schemas";
import { School } from "src/scenes/orgAdmin/formTemplates/RulesTab/FormEngineRuleDrawer/shared";
import * as GQL from "src/types/graphql";

// Status/substatus options
const statusOptions = [
  // GQL.form_status_enum is not listed here because form operations are not fully integrated with form-service
  // Offer statuses
  { id: GQL.offer_status_enum.Offered, label: GQL.offer_status_enum.Offered },
  { id: GQL.offer_status_enum.Accepted, label: GQL.offer_status_enum.Accepted },
  { id: GQL.offer_status_enum.Declined, label: GQL.offer_status_enum.Declined },
  { id: GQL.offer_status_enum.Revoked, label: GQL.offer_status_enum.Revoked },
  // Waitlist statuses
  {
    id: GQL.waitlist_status_enum.Waitlisted,
    label: GQL.waitlist_status_enum.Waitlisted,
  },
  {
    id: GQL.waitlist_status_enum.Withdrawn,
    label: GQL.waitlist_status_enum.Withdrawn,
  },
  {
    id: GQL.waitlist_status_enum.Removed,
    label: GQL.waitlist_status_enum.Removed,
  },
];

export type DynamicSiblingRuleProps = {
  formik: FormikProps<FormEngineRuleFormData>;
  schools: School[];
};

export const DynamicSiblingRule: React.FC<DynamicSiblingRuleProps> = ({
  formik,
  schools,
}) => {
  const schoolErrorById = useSchoolErrorById();
  const statusError = useFieldError("status");

  useEffect(() => {
    formik.setFieldValue(
      "rule_type",
      GQL.form_engine_rule_type_enum.DYNAMIC_SIBLING_TAG
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* end trigger condition section */}
      {/* start school filter section */}
      <FormControl>
        <FormLabel pb="0.325rem" m="0">
          Where should the sibling be in?
        </FormLabel>
        <MultiSelectInput
          size="md"
          options={schools.map((school) => ({
            id: school.id,
            label: school.name,
          }))}
          initialValues={formik.values.school_ids.map((schoolId) => {
            const school = schools.find((s) => s.id === schoolId);
            return {
              id: schoolId,
              label: school ? school.name : schoolId,
            };
          })}
          onChange={(selectedSchools) => {
            formik.setFieldValue(
              "school_ids",
              selectedSchools.map((s) => s.id)
            );
          }}
          placeholder="Select multiple"
          error={schoolErrorById.size > 0 ? SCHOOL_CONFLICT_SUMMARY : undefined}
          isInvalidItem={(item) => schoolErrorById.has(item.id)}
          getInvalidItemMessage={(item) => schoolErrorById.get(item.id)}
          renderDropdownItem={(item) => <Flex key={item.id}>{item.label}</Flex>}
        />
      </FormControl>
      <Text fontSize="sm" color="gray.600">
        The rule will consider only siblings in the selected schools. If no
        schools are selected, it will consider siblings who share any one
        school.
      </Text>
      {/* end school filter section */}
      {/* start status filter section */}
      <Flex direction="column" gap="1.5rem">
        <FormControl isInvalid={statusError !== undefined}>
          <FormLabel pb="0.5rem" m="0">
            Which sub-status should the siblings have for the tag to be applied?
          </FormLabel>
          <MultiSelectInput
            size="md"
            options={statusOptions.map((option) => ({
              id: option.id,
              label: option.label,
            }))}
            initialValues={formik.values.status.map((status) => {
              const selectedStatus = statusOptions.find((s) => s.id === status);
              return {
                id: status,
                label: selectedStatus?.label ?? "<UNKNOWN>",
              };
            })}
            onChange={(status) => {
              formik.setFieldValue(
                "status",
                status.map((s) => s.id)
              );
            }}
            placeholder="Select multiple"
            renderDropdownItem={(item) => (
              <Flex key={item.id}>{item.label}</Flex>
            )}
            error={statusError}
          />
        </FormControl>
        <FormControl>
          <CheckboxControl
            name="enable_remove_tag"
            value={1}
            isChecked={formik.values.enable_remove_tag}
          >
            Remove tag when this condition is not met
          </CheckboxControl>
        </FormControl>
      </Flex>
      {/* end status filter section */}
    </>
  );
};
